import type { Staged } from '~/core/router'

import { api, LiveEventsTypes } from '@setplex/tria-api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { combineEvents, not, or } from 'patronum'
import { reset } from 'patronum/reset'
import { options } from '~/core/flags'
import { register } from '~/core/repository'
import { model as eventsFeatureModel } from '~/features/events'
import { model as guest } from '~/features/subscriber/guest'
import { selectBestTab } from '~/shared/effector'
import { $liveEventsTranslations } from './translations'

export const initAllEventsPage = createEvent<{
  query: URLSearchParams
  stage: Staged['stage']
}>()

export const leftAllEventsPage = createEvent()

export const loadAllEvents = createEvent()

const initLiveEventsFx = attach({ effect: api.liveEvents.initLiveEventsFx })
const initUpcomingEventsFx = attach({
  effect: api.liveEvents.initUpcomingEventsFx,
})
const initPastEventsFx = attach({ effect: api.liveEvents.initPastEventsFx })

export const $hasLiveEvent = createStore<boolean>(false)
export const $hasUpcomingEvent = createStore<boolean>(false)
export const $hasPastEvent = createStore<boolean>(false)

sample({
  clock: initLiveEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasLiveEvent,
})
sample({
  clock: initUpcomingEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasUpcomingEvent,
})
sample({
  clock: initPastEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasPastEvent,
})

/********** Skeleton logics starts ***********/

const $isAnyInitFxPending = or(
  initLiveEventsFx.pending,
  initUpcomingEventsFx.pending,
  initPastEventsFx.pending
)

const $activeTabHasContent = combine(
  {
    activeTab: eventsFeatureModel.$activeTab,
    live: eventsFeatureModel.$allEvents.map(
      (events) =>
        events.find(({ contentType }) => contentType === LiveEventsTypes.LIVE)
          ?.list.length
    ),
    upcoming: eventsFeatureModel.$allEvents.map(
      (events) =>
        events.find(
          ({ contentType }) => contentType === LiveEventsTypes.UPCOMING
        )?.list.length
    ),
    past: eventsFeatureModel.$allEvents.map(
      (events) =>
        events.find(({ contentType }) => contentType === LiveEventsTypes.PAST)
          ?.list.length
    ),
  },
  ({ activeTab, live, upcoming, past }) => {
    if (activeTab === LiveEventsTypes.LIVE) return Boolean(live)
    if (activeTab === LiveEventsTypes.UPCOMING) return Boolean(upcoming)
    if (activeTab === LiveEventsTypes.PAST) return Boolean(past)
    return false
  }
)

export const $contentLoading = combine(
  $isAnyInitFxPending,
  $activeTabHasContent,
  (pending, activeTabHasContent) => pending && !activeTabHasContent
)
/********** Skeleton logics ends ***********/

sample({
  clock: initAllEventsPage,
  filter: not($isAnyInitFxPending),
  target: loadAllEvents,
})

sample({
  clock: loadAllEvents,
  filter: options.$liveEventRecordsEnabled,
  target: [initLiveEventsFx, initUpcomingEventsFx, initPastEventsFx],
})

sample({
  clock: loadAllEvents,
  filter: not(options.$liveEventRecordsEnabled),
  target: [initLiveEventsFx, initUpcomingEventsFx],
})

const allTabsLoadedWithPast = combineEvents({
  events: {
    live: initLiveEventsFx.finally,
    upcoming: initUpcomingEventsFx.finally,
    past: initPastEventsFx.finally,
  },
})

const allTabsLoadedWithoutPast = combineEvents({
  events: {
    live: initLiveEventsFx.finally,
    upcoming: initUpcomingEventsFx.finally,
  },
})

export const $eventsCatalogSettled = createStore(false)

sample({
  clock: allTabsLoadedWithPast,
  filter: options.$liveEventRecordsEnabled,
  fn: () => true,
  target: $eventsCatalogSettled,
})

sample({
  clock: allTabsLoadedWithoutPast,
  filter: not(options.$liveEventRecordsEnabled),
  fn: () => true,
  target: $eventsCatalogSettled,
})

export const $isEventsEmptyPage = combine(
  $eventsCatalogSettled,
  eventsFeatureModel.$isAnyEvents,
  (settled, isAnyEvents) => settled && !isAnyEvents
)

sample({
  clock: allTabsLoadedWithPast,
  source: {
    live: $hasLiveEvent,
    upcoming: $hasUpcomingEvent,
    past: $hasPastEvent,
    preferredTab: eventsFeatureModel.$preferredTab,
  },
  filter: ({ live, upcoming, past }) => live || upcoming || past,
  fn: ({ live, upcoming, past, preferredTab }) => ({
    // filter above ensures at least one tab has content
    id: selectBestTab(
      [LiveEventsTypes.LIVE, LiveEventsTypes.UPCOMING, LiveEventsTypes.PAST],
      {
        [LiveEventsTypes.LIVE]: live,
        [LiveEventsTypes.UPCOMING]: upcoming,
        [LiveEventsTypes.PAST]: past,
      },
      preferredTab
    )!,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

sample({
  clock: allTabsLoadedWithoutPast,
  source: {
    live: $hasLiveEvent,
    upcoming: $hasUpcomingEvent,
    pastEventsEnabled: options.$liveEventRecordsEnabled,
    preferredTab: eventsFeatureModel.$preferredTab,
  },
  filter: ({ live, upcoming, pastEventsEnabled }) =>
    !pastEventsEnabled && (live || upcoming),
  fn: ({ live, upcoming, preferredTab }) => ({
    // filter above ensures at least one tab has content
    id: selectBestTab(
      [LiveEventsTypes.LIVE, LiveEventsTypes.UPCOMING],
      {
        [LiveEventsTypes.LIVE]: live,
        [LiveEventsTypes.UPCOMING]: upcoming,
        [LiveEventsTypes.PAST]: false,
      } satisfies Record<LiveEventsTypes, boolean>,
      preferredTab
    )!,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

reset({
  // need to reset data on user logsIn or logsOut
  clock: guest.$guest,
  target: [
    api.liveEvents.$liveEvents,
    api.liveEvents.$upcomingEvents,
    api.liveEvents.$pastEvents,
  ],
})

eventsFeatureModel.$activeTab.reset(guest.$guest)
eventsFeatureModel.$preferredTab.reset(guest.$guest)
$eventsCatalogSettled.reset(guest.$guest)

register(eventsFeatureModel.$isAnyEvents, '$events/isAnyEvents')
register($isEventsEmptyPage, '$isEventsEmptyPage')
register(eventsFeatureModel.changeActiveTab, 'events/changeActiveTab')
register(eventsFeatureModel.$formatActiveTab, '$formatActiveTab')
register(eventsFeatureModel.$activeTab, '$events/activeTab')
register(eventsFeatureModel.$allEvents, '$events/allEvents')
register(eventsFeatureModel.loadMoreEvents, 'events/loadMoreEvents')
register($liveEventsTranslations, '$liveEvents/liveEventsTranslations')
